<template>
  <div class="escalation-dashboard">
    <Head title="Escalation Dashboard" />

    <div class="py-12">
      <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
        <div class="bg-white overflow-hidden shadow-sm sm:rounded-lg">
          <div class="p-6 bg-white border-b border-gray-200">
            <h2 class="text-2xl font-bold text-gray-900 mb-6">Escalation Dashboard</h2>

            <!-- Stats Cards -->
            <div class="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
              <div class="bg-blue-50 p-4 rounded-lg">
                <h3 class="text-sm font-medium text-blue-600">Total Escalated</h3>
                <p class="text-2xl font-bold text-blue-900">{{ stats.total_escalated }}</p>
              </div>
              <div class="bg-green-50 p-4 rounded-lg">
                <h3 class="text-sm font-medium text-green-600">Level 1</h3>
                <p class="text-2xl font-bold text-green-900">{{ stats.level_1 }}</p>
              </div>
              <div class="bg-yellow-50 p-4 rounded-lg">
                <h3 class="text-sm font-medium text-yellow-600">Level 2</h3>
                <p class="text-2xl font-bold text-yellow-900">{{ stats.level_2 }}</p>
              </div>
              <div class="bg-orange-50 p-4 rounded-lg">
                <h3 class="text-sm font-medium text-orange-600">Level 3</h3>
                <p class="text-2xl font-bold text-orange-900">{{ stats.level_3 }}</p>
              </div>
              <div class="bg-red-50 p-4 rounded-lg">
                <h3 class="text-sm font-medium text-red-600">Level 4</h3>
                <p class="text-2xl font-bold text-red-900">{{ stats.level_4 }}</p>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex space-x-4 mb-6">
              <Link
                :href="route('escalation.index')"
                class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              >
                Manage Escalation Matrix
              </Link>
              <Link
                :href="this.route('escalation.create')"
                class="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
              >
                Add New Matrix
              </Link>
            </div>

            <!-- Recent Escalations -->
            <div class="bg-gray-50 rounded-lg p-6">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">Recent Escalations</h3>
              <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50">
                    <tr>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ticket ID
                      </th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Subject
                      </th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Priority
                      </th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Level
                      </th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Escalated At
                      </th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="ticket in recentEscalations" :key="ticket.id" class="hover:bg-gray-50">
                      <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{{ ticket.id }}
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {{ ticket.subject }}
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap">
                        <span :class="getPriorityClass(ticket.priority)" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                          {{ ticket.priority }}
                        </span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap">
                        <span :class="getLevelClass(ticket.escalation_level)" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                          Level {{ ticket.escalation_level }}
                        </span>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {{ ticket.customer?.name || 'N/A' }}
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {{ formatDate(ticket.escalation?.escalated_at) }}
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <Link
                          :href="route('tickets.show', ticket.id)"
                          class="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          View
                        </Link>
                        <button
                          class="text-red-600 hover:text-red-900"
                          :disabled="processing"
                          @click="escalateTicket(ticket.id)"
                        >
                          Escalate
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link, router } from '@inertiajs/vue3'
//import { route } from 'ziggy-js'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import SearchInput from '@/Shared/SearchInput'

export default {
  name: 'EscalationDashboard',
  components: {
    Head,
    Link,
  },
  props: {
    stats: Object,
    recentEscalations: Array,
  },
  data() {
    return {
      processing: false,
    }
  },
  methods: {
    getPriorityClass(priority) {
      const classes = {
        low: 'bg-green-100 text-green-800',
        medium: 'bg-yellow-100 text-yellow-800',
        high: 'bg-orange-100 text-orange-800',
        critical: 'bg-red-100 text-red-800',
      }
      return classes[priority] || 'bg-gray-100 text-gray-800'
    },
    getLevelClass(level) {
      const classes = {
        1: 'bg-blue-100 text-blue-800',
        2: 'bg-yellow-100 text-yellow-800',
        3: 'bg-orange-100 text-orange-800',
        4: 'bg-red-100 text-red-800',
      }
      return classes[level] || 'bg-gray-100 text-gray-800'
    },
    formatDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    escalateTicket(ticketId) {
      if (this.processing) return

      this.processing = true
      router.post(`/tickets/${ticketId}/escalate`, {}, {
        onSuccess: () => {
          this.processing = false
        },
        onError: () => {
          this.processing = false
        },
      })
    },
  },
}
</script>

<style scoped>
.escalation-dashboard {
  min-height: 100vh;
  background-color: #f9fafb;
}
</style>
